import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, X, Send, User, Mic, MicOff, FileText, Volume2, VolumeX, Maximize2, Minimize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { findBestMatch } from '../utils/nlp';
import faqsData from '../assets/faqs.json';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import 'regenerator-runtime/runtime';
import { AI_API_BASE_URL } from '../utils/api';

// ──────────────────────────────────────────────
// Message interface
// ──────────────────────────────────────────────
interface Message {
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
    link?: string | undefined;
    linkText?: string | undefined;
}

// ──────────────────────────────────────────────
// Chatbot Component
// ──────────────────────────────────────────────
const Chatbot: React.FC = () => {
    const navigate = useNavigate();


    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isIconAnimating, setIsIconAnimating] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
    const [isMaximized, setIsMaximized] = useState<boolean>(false);
    const [isSpeechEnabled, setIsSpeechEnabled] = useState<boolean>(false);
    const [position, setPosition] = useState({ x: window.innerWidth - 358, y: window.innerHeight - 565 });
    const [width, setWidth] = useState<number>(350);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [resizeStartX, setResizeStartX] = useState<number>(0);
    const [resizeStartWidth, setResizeStartWidth] = useState<number>(350);
    const [messages, setMessages] = useState<Message[]>([
        {
            id: 'welcome',
            text: "Hello! I'm your devNectar assistant. Ask me anything or tap the 🎤 mic to speak!",
            sender: 'bot' as const,
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [suggestedOptions, setSuggestedOptions] = useState<string[]>(['Services', 'Pricing', 'Portfolio', 'Contact', 'Technologies', 'Team', 'CEO', 'Support', 'Office Location']);
    
    const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);

    // Pre-fetch & cache natural voices when Web Speech API loads
    useEffect(() => {
        if (!('speechSynthesis' in window)) return;
        const updateVoices = () => {
            const v = window.speechSynthesis.getVoices();
            if (v.length > 0) setAvailableVoices(v);
        };
        updateVoices();
        window.speechSynthesis.onvoiceschanged = updateVoices;
    }, []);

    const speakText = useCallback((text: string) => {
        if (!('speechSynthesis' in window)) return;
        window.speechSynthesis.cancel();
        if (!text) return;

        // Clean text for natural speech pronunciation
        const cleanText = text
            .replace(/https?:\/\/\S+/gi, '') // Remove URLs
            .replace(/[*#_~`>]/g, '') // Remove markdown symbols
            .replace(/[-•]\s+/g, '') // Remove bullets
            .replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '') // Remove emojis
            .replace(/\s+/g, ' ')
            .trim();

        if (!cleanText) return;

        const utterance = new SpeechSynthesisUtterance(cleanText);
        utterance.rate = 0.92; // Natural, smooth speaking rate
        utterance.pitch = 1.0;  // Balanced warm tone

        const voices = availableVoices.length > 0 ? availableVoices : window.speechSynthesis.getVoices();

        // Select the smoothest natural human voice available
        const smoothVoice = voices.find(v => 
            v.lang.startsWith('en') && (
                v.name.includes('Natural') ||
                v.name.includes('Google US English') ||
                v.name.includes('Google UK English') ||
                v.name.includes('Google') ||
                v.name.includes('Samantha') ||
                v.name.includes('Jenny') ||
                v.name.includes('Aria') ||
                v.name.includes('Guy') ||
                v.name.includes('Karen') ||
                v.name.includes('Zira')
            )
        ) || voices.find(v => v.lang.startsWith('en')) || voices[0];

        if (smoothVoice) {
            utterance.voice = smoothVoice;
        }

        window.speechSynthesis.speak(utterance);
    }, [availableVoices]);

    useEffect(() => {
        if (!isSpeechEnabled || messages.length === 0) return;
        const lastMsg = messages[messages.length - 1];
        if (lastMsg && lastMsg.sender === 'bot') {
            speakText(lastMsg.text);
        }
    }, [messages, isSpeechEnabled, speakText]);

    const toggleSpeech = () => {
        if (isSpeechEnabled) {
            setIsSpeechEnabled(false);
            if ('speechSynthesis' in window) window.speechSynthesis.cancel();
        } else {
            setIsSpeechEnabled(true);
            const lastBotMessage = [...messages].reverse().find(m => m.sender === 'bot');
            if (lastBotMessage) {
                speakText(lastBotMessage.text);
            }
        }
    };

    const toggleMaximize = () => {
        setIsMaximized(prev => !prev);
    };
    
    // Voice Recognition hook
    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();

    const dispatchRef = useRef<(text: string) => void>(() => { });

    const chatRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping, transcript]);


    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 640;
            setIsMobile(mobile);
            if (!isOpen) {
                setPosition({ x: window.innerWidth - 80, y: window.innerHeight - 80 });
            } else if (mobile) {
                setPosition({ x: 0, y: 0 });
            } else {
                setPosition({ x: window.innerWidth - (width + 8), y: window.innerHeight - 565 });
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen, width]);

    useEffect(() => {
        if (!isOpen && listening) {
            SpeechRecognition.stopListening();
        }
    }, [isOpen, listening]);

    // Handle incoming transcript
    useEffect(() => {
        if (transcript) {
            setInputValue(transcript);
        }
    }, [transcript]);

    // Global event listener to open chatbot from anywhere
    useEffect(() => {
        const handleOpenChatbot = () => {
            if (window.innerWidth < 640) {
                navigate('/chat');
                return;
            }
            setIsOpen(true);
            setPosition({ x: window.innerWidth - (width + 8), y: window.innerHeight - 565 });
        };
        window.addEventListener('open-devnectar-chatbot', handleOpenChatbot);
        return () => window.removeEventListener('open-devnectar-chatbot', handleOpenChatbot);
    }, [navigate, width]);


    const toggleChat = () => {
        if (window.innerWidth < 640) {
            navigate('/chat');
            return;
        }
        if (!isOpen) {
            setIsIconAnimating(true);
            setTimeout(() => {
                setIsOpen(true);
                setIsIconAnimating(false);
                setPosition({ x: window.innerWidth - (width + 8), y: window.innerHeight - 565 });
            }, 250);
        } else {
            setIsOpen(false);
        }
    };

    const handleSend = useCallback(async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;

        const userMsg: Message = {
            id: Date.now().toString(),
            text: trimmed,
            sender: 'user',
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMsg]);
        setInputValue('');
        setIsTyping(true);

        try {
            const response = await fetch(`${AI_API_BASE_URL}/chatbot/query`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question: trimmed })
            });

            if (!response.ok) throw new Error("Backend unavailable");
            const data = await response.json();

            if (data.match) {
                const match = data.match.toLowerCase();
                if (match.includes('service')) setSuggestedOptions(['Pricing', 'Technologies', 'Contact']);
                else if (match.includes('ceo') || match.includes('team')) setSuggestedOptions(['Services', 'Contact']);
            }

            if (data.isUnknown) {
                setSuggestedOptions(['Services', 'Contact', 'Pricing']);
                setMessages(prev => [...prev, {
                    id: (Date.now() + 1).toString(),
                    text: data.answer,
                    sender: 'bot',
                    timestamp: new Date()
                }]);
                setIsTyping(false);
            } else if (data.redirect) {
                setMessages(prev => [...prev, { 
                    id: (Date.now() + 1).toString(), 
                    text: `${data.answer}`, 
                    sender: 'bot', 
                    timestamp: new Date(),
                    link: data.redirect,
                    linkText: data.linkText || `View ${data.redirect.replace('/', '') || 'page'}`
                }]);
                setIsTyping(false);
            } else {
                setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), text: data.answer, sender: 'bot', timestamp: new Date() }]);
                setIsTyping(false);
            }
        } catch (error) {
            console.warn("Backend offline, falling back to local.");
            const localMatch = findBestMatch(trimmed, faqsData as any[]);
            if (localMatch) {
                setSuggestedOptions(['Services', 'Contact', 'Pricing']);
                setMessages(prev => [...prev, { 
                    id: (Date.now() + 1).toString(), 
                    text: localMatch.answer, 
                    sender: 'bot', 
                    timestamp: new Date(),
                    link: localMatch.redirect,
                    linkText: localMatch.redirect ? `Go to ${localMatch.redirect.replace('/', '')}` : undefined
                }]);
            } else {
                setSuggestedOptions(['Services', 'Contact']);
                setMessages(prev => [...prev, {
                    id: (Date.now() + 1).toString(),
                    text: "Sorry, I don't have this knowledge in my knowledge base. Please contact our team for further help.",
                    sender: 'bot',
                    timestamp: new Date()
                }]);
            }
            setIsTyping(false);
        }
    }, []);

    useEffect(() => {
        dispatchRef.current = (text: string) => handleSend(text);
    }, [handleSend]);

    // Auto-send when user stops speaking (pause detected by browser)
    useEffect(() => {
        if (!listening && transcript.trim().length > 0) {
            // Small delay to ensure the UI updates before sending
            const timer = setTimeout(() => {
                if (transcript.trim()) {
                    handleSend(transcript);
                    resetTranscript();
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [listening, transcript, handleSend, resetTranscript]);

    const handleSendInput = () => {
        const textToSend = inputValue || transcript;
        if (textToSend) {
            handleSend(textToSend);
            resetTranscript();
        }
    };

    const toggleListening = () => {
        if (listening) {
            SpeechRecognition.stopListening();
            // The useEffect will handle sending if there's a transcript
        } else {
            setInputValue('');
            resetTranscript();
            // Changed to continuous: false so it stops automatically after a pause
            SpeechRecognition.startListening({ continuous: false, language: 'en-US' });
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);

        // Step 1: Show the uploaded file as a user-side message (right)
        const fileMsg: Message = {
            id: `file-${Date.now()}`,
            text: `📎 ${file.name}`,
            sender: 'user',
            timestamp: new Date()
        };
        setMessages(prev => [...prev, fileMsg]);
        setIsTyping(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await fetch(`${AI_API_BASE_URL}/chatbot/upload`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) throw new Error("Upload failed");
            const data = await response.json();
            const keywords: string[] = data.keywords || [];

            // Step 2: Bot shows extracted keywords (left side)
            const kwDisplay = keywords.length > 0 ? keywords.join(', ') : 'none found';
            setMessages(prev => [...prev, {
                id: `kw-${Date.now()}`,
                text: `I've processed **${file.name}**. Extracted keywords: **${kwDisplay}**.`,
                sender: 'bot',
                timestamp: new Date()
            }]);

            // Step 3: Auto-answer using the top keyword from the knowledge base
            if (keywords.length > 0) {
                // Small delay to make it feel like the bot is thinking
                await new Promise(res => setTimeout(res, 700));
                // Use the first keyword to fetch a real FAQ answer (skip document search)
                const queryResponse = await fetch(`${AI_API_BASE_URL}/chatbot/query`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ question: keywords[0], skipDocSearch: true })
                });
                if (queryResponse.ok) {
                    const qData = await queryResponse.json();
                    if (qData.answer) {
                        setMessages(prev => [...prev, {
                            id: `ans-${Date.now()}`,
                            text: qData.answer,
                            sender: 'bot',
                            timestamp: new Date(),
                            link: qData.redirect || undefined,
                            linkText: qData.linkText || undefined
                        }]);
                    }
                }
            }
        } catch (error) {
            console.error("Upload error:", error);
            setMessages(prev => [...prev, {
                id: Date.now().toString(),
                text: "Sorry, I couldn't process that file. Please try again with a PDF, Word, or image file.",
                sender: 'bot',
                timestamp: new Date()
            }]);
        } finally {
            setIsUploading(false);
            setIsTyping(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // Pin position to bottom-right corner when width or open state changes
    useEffect(() => {
        if (!isMobile && isOpen) {
            setPosition({ x: window.innerWidth - (width + 13), y: window.innerHeight - 545 });
        }
    }, [width, isOpen, isMobile]);

    const handleResizeMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        setResizeStartX(e.clientX);
        setResizeStartWidth(width);
    };

    const handleResizeMouseMove = useCallback((e: MouseEvent) => {
        if (isResizing) {
            const deltaX = e.clientX - resizeStartX;
            const newWidth = resizeStartWidth - deltaX;
            const minWidth = 350;
            const maxWidth = window.innerWidth * 0.5;
            const constrainedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));
            
            setWidth(constrainedWidth);
            setPosition(prev => ({ ...prev, x: window.innerWidth - (constrainedWidth + 13) }));
        }
    }, [isResizing, resizeStartX, resizeStartWidth]);

    const handleResizeMouseUp = () => {
        setIsResizing(false);
    };

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleResizeMouseMove);
            window.addEventListener('mouseup', handleResizeMouseUp);
        } else {
            window.removeEventListener('mousemove', handleResizeMouseMove);
            window.removeEventListener('mouseup', handleResizeMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleResizeMouseMove);
            window.removeEventListener('mouseup', handleResizeMouseUp);
        };
    }, [isResizing, handleResizeMouseMove]);

    // RENDER
    return (
        <>
            {/* Floating Icon */}
            {!isOpen && (
                <div className="fixed bottom-[20px] right-[20px] z-50 flex items-center group select-none">
                    {/* Text bubble displayed on hover */}
                    <button
                        onClick={toggleChat}
                        className="opacity-0 translate-x-4 pointer-events-none group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto transition-all duration-300 bg-white text-blue-600 font-bold px-4 py-2 rounded-full shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-blue-100 mr-2 whitespace-nowrap text-sm cursor-pointer"
                    >
                        Chat with Bot
                    </button>

                    <button
                        onClick={toggleChat}
                        className={`w-[55px] h-[55px] bg-white text-blue-600 rounded-full shadow-2xl border-2 border-blue-600 transition-all duration-300 flex items-center justify-center
                            ${isIconAnimating ? 'animate-click-bounce' : 'hover:scale-105 active:scale-95 animate-[pulse-ring_2s_infinite_cubic-bezier(0.25,0.8,0.25,1)]'}
                        `}
                    >
                        <div className="absolute inset-0 rounded-full bg-blue-100 opacity-0 hover:opacity-20 transition-opacity -z-10"></div>
                        <Bot size={28} strokeWidth={2.5} className="group-hover:animate-[wave_1s_ease-in-out_infinite] transition-transform" />
                    </button>
                </div>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div
                    ref={chatRef}
                    className={`fixed z-[9999] bg-white shadow-2xl flex flex-col overflow-hidden border border-gray-100 animate-chat-slide-in
                        ${isMaximized
                            ? isMobile
                                ? 'inset-x-0 bottom-0 top-[60px] rounded-t-3xl rounded-b-none w-full'
                                : 'top-[62px] bottom-0 right-0 w-[50vw] rounded-2xl shadow-2xl'
                            : isMobile
                                ? 'inset-x-0 bottom-0 top-auto h-[80vh] rounded-t-3xl rounded-b-none w-full'
                                : 'h-[555px] rounded-2xl'
                        }
                    `}
                    style={isMaximized ? {
                        width: isMobile ? '100%' : '50vw',
                        top: isMobile ? '60px' : '62px',
                        bottom: '0px',
                        right: '0px',
                        left: 'auto',
                        transition: 'all 0.25s ease-in-out'
                    } : {
                        left: isMobile ? '0' : `${position.x}px`,
                        top: isMobile ? 'auto' : `${position.y}px`,
                        bottom: isMobile ? '0' : '12px',
                        right: isMobile ? '0' : '8px',
                        width: isMobile ? '100%' : `${width}px`,
                        transition: isResizing ? 'none' : 'all 0.1s ease-out',
                        transformOrigin: 'bottom right'
                    }}
                >
                    {/* Left resize handle */}
                    {!isMobile && !isMaximized && (
                        <div
                            onMouseDown={handleResizeMouseDown}
                            className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize z-50 hover:bg-blue-500/20 active:bg-blue-500/40 transition-colors"
                        />
                    )}
                    {/* Header */}
                    <div
                        className="bg-blue-700 p-4 flex items-center justify-between select-none"
                    >
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 rounded-lg">
                                <Bot size={20} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-sm leading-tight">devNectar bot</h3>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <div className={`w-1.5 h-1.5 rounded-full shadow-[0_0_5px_rgba(74,222,128,0.6)] ${listening ? 'bg-red-400 animate-pulse' : (isTyping ? 'bg-amber-400' : 'bg-green-400')}`}></div>
                                    <p className="text-blue-100/80 text-[10px] font-semibold tracking-wider uppercase">
                                        {listening ? 'Listening' : (isTyping ? 'Thinking' : 'Online')}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <button
                                onClick={toggleSpeech}
                                title={isSpeechEnabled ? "Mute Bot Voice" : "Enable Bot Voice"}
                                className="p-1.5 hover:bg-white/20 rounded-md text-white transition-colors flex items-center justify-center cursor-pointer"
                            >
                                {isSpeechEnabled ? (
                                    <Volume2 size={18} className="text-emerald-300 animate-pulse" />
                                ) : (
                                    <VolumeX size={18} className="text-white/75 hover:text-white" />
                                )}
                            </button>
                            <button
                                onClick={toggleMaximize}
                                title={isMaximized ? "Restore Size" : "Maximize Chat"}
                                className="p-1.5 hover:bg-white/20 rounded-md text-white transition-colors flex items-center justify-center cursor-pointer"
                            >
                                {isMaximized ? (
                                    <Minimize2 size={18} className="text-white" />
                                ) : (
                                    <Maximize2 size={18} className="text-white" />
                                )}
                            </button>
                            <button
                                onClick={toggleChat}
                                title="Close Chat"
                                className="p-1.5 hover:bg-white/20 rounded-md text-white transition-colors flex items-center justify-center cursor-pointer ml-0.5"
                            >
                                <X size={18} className="text-white" />
                            </button>
                        </div>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 bg-gray-50 p-4 overflow-y-auto flex flex-col gap-4">
                        {messages.map((msg) => (
                            <div key={msg.id} className={`flex gap-2 max-w-[85%] ${msg.sender === 'user' ? 'self-end flex-row-reverse' : ''}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.sender === 'bot' ? 'bg-blue-100' : 'bg-gray-200'}`}>
                                    {msg.sender === 'bot' ? <Bot size={16} className="text-blue-600" /> : <User size={16} className="text-gray-600" />}
                                </div>
                                <div className={`p-3 rounded-2xl shadow-sm border ${msg.sender === 'bot'
                                    ? 'bg-white rounded-tl-none border-gray-100 text-gray-700'
                                    : 'bg-blue-700 rounded-tr-none border-blue-600 text-white'
                                    }`}>
                                    <div className="text-sm leading-relaxed space-y-1">
                                        {msg.text.split('\n').map((line, i) => {
                                            // Blockquote lines starting with >
                                            if (line.startsWith('> ')) {
                                                return (
                                                    <blockquote key={i} className="border-l-2 border-blue-300 pl-2 text-gray-500 italic text-xs">
                                                        {line.slice(2).replace(/\*\*(.*?)\*\*/g, '$1')}
                                                    </blockquote>
                                                );
                                            }
                                            // Bold + regular inline text
                                            const parts = line.split(/\*\*(.*?)\*\*/g);
                                            return (
                                                <p key={i}>
                                                    {parts.map((part, j) =>
                                                        j % 2 === 1
                                                            ? <strong key={j} className="font-semibold text-gray-800">{part}</strong>
                                                            : <span key={j}>{part}</span>
                                                    )}
                                                </p>
                                            );
                                        })}
                                    </div>
                                    
                                    {msg.link && (
                                        <button
                                            onClick={() => {
                                                if (msg.link?.startsWith('http')) {
                                                    window.open(msg.link, '_blank');
                                                } else {
                                                    navigate(msg.link!);
                                                    if (window.innerWidth < 640) setIsOpen(false);
                                                }
                                            }}
                                            className="mt-3 w-full py-2 px-4 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 border border-blue-100 group/btn"
                                        >
                                            <span className="flex-1 truncate text-center">{msg.linkText || 'Explore'}</span>
                                            {msg.link?.startsWith('http') ? (
                                                <div className="p-1 bg-blue-600 rounded-md group-hover/btn:bg-blue-700 transition-colors">
                                                    <FileText size={12} className="text-white" />
                                                </div>
                                            ) : (
                                                <Send size={12} className="rotate-45" />
                                            )}
                                        </button>
                                    )}

                                    <span className={`text-[10px] mt-1 block ${msg.sender === 'bot' ? 'text-gray-400' : 'text-blue-200 text-right'}`}>
                                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            </div>
                        ))}

                        {/* Typing indicator */}
                        {isTyping && (
                            <div className="flex gap-2 max-w-[85%]">
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                    <Bot size={16} className="text-blue-600" />
                                </div>
                                <div className="bg-white p-3 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 flex gap-1 items-center">
                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                                </div>
                            </div>
                        )}

                        {/* Live voice transcript indicator */}
                        {listening && transcript && (
                            <div className="flex gap-2 max-w-[85%] self-end flex-row-reverse opacity-60">
                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                    <Mic size={14} className="text-blue-500" />
                                </div>
                                <div className="bg-blue-100 p-3 rounded-2xl rounded-tr-none border border-blue-200 text-blue-700 text-sm italic">
                                    {transcript}...
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Dynamic Quick Options */}
                    <div className="px-4 py-3 flex gap-2 overflow-x-auto bg-white border-t border-gray-50 scrollbar-custom">
                        {messages.length > 3 && (
                            <button
                                onClick={() => setMessages([{ id: 'welcome', text: "Chat cleared. How can I help you now?", sender: 'bot', timestamp: new Date() }])}
                                className="whitespace-nowrap px-3 py-1 bg-red-50 hover:bg-red-100 text-red-600 text-xs rounded-full border border-red-100 transition-colors flex items-center gap-1"
                            >
                                <X size={10} /> Clear
                            </button>
                        )}
                        {suggestedOptions.map((opt) => (
                            <button
                                key={opt}
                                onClick={() => {
                                    if (opt === 'Contact') handleSend('How do I start a project with devNectar?');
                                    else if (opt === 'Services') handleSend('What services do you provide?');
                                    else if (opt === 'Portfolio') handleSend('Can I see your portfolio of projects?');
                                    else if (opt === 'Team') handleSend('How many employees are in the devNectar team?');
                                    else if (opt === 'Pricing') handleSend('What is your pricing model?');
                                    else if (opt === 'Technologies') handleSend('What technologies do you work with?');
                                    else handleSend(opt); // Generic case
                                }}
                                className="whitespace-nowrap px-3 py-1 bg-blue-50/50 hover:bg-blue-100/80 text-blue-700 text-xs font-medium rounded-full border border-blue-100/50 transition-all active:scale-95"
                            >
                                {opt}
                            </button>
                        ))}
                    </div>

                    {/* Input Area */}
                    <form
                        className="pt-2.5 pb-2 px-3 bg-white border-t border-gray-100 flex-shrink-0"
                        onSubmit={(e) => { e.preventDefault(); handleSendInput(); }}
                    >
                        <div className="relative flex items-center gap-2">
                            <input
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder={listening ? 'Listening… speak now 🎤' : 'Type your message...'}
                                className={`flex-1 pl-3.5 pr-3.5 py-2 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm transition-all
                                    ${listening
                                        ? 'border-blue-400 bg-blue-50 placeholder-blue-400 ring-2 ring-blue-300'
                                        : 'border-gray-200'
                                    }
                                `}
                            />

                            {/* Mic button — only shown if browser supports it */}
                            {browserSupportsSpeechRecognition && (
                                <button
                                    type="button"
                                    onClick={toggleListening}
                                    title={listening ? 'Stop listening' : 'Speak your question'}
                                    className={`flex-shrink-0 p-2 rounded-xl transition-all duration-200 border
                                        ${listening
                                            ? 'bg-red-500 border-red-500 text-white animate-mic-pulse shadow-red-300 shadow-lg'
                                            : 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100 hover:text-blue-700'
                                        }
                                    `}
                                >
                                    {listening ? <MicOff size={16} /> : <Mic size={16} />}
                                </button>
                            )}

                            {/* Send button */}
                            <button
                                type="submit"
                                className="flex-shrink-0 p-2 bg-blue-700 text-white rounded-xl hover:bg-blue-800 transition-colors"
                            >
                                <Send size={16} />
                            </button>
                        </div>

                        {/* Voice status hint */}
                        {listening && (
                            <p className="text-[10px] text-blue-500 mt-1 text-center animate-pulse">
                                🎤 Listening... speak clearly and pause when done
                            </p>
                        )}
                        {!browserSupportsSpeechRecognition && (
                            <p className="text-[10px] text-gray-400 mt-1 text-center">
                                Voice input not supported in this browser
                            </p>
                        )}
                        <p className="text-xs text-gray-600 font-semibold text-center mt-1 pb-1 tracking-wide select-none">
                            Powered by{' '}
                            <a
                                href="https://devnectar.in"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-extrabold text-blue-600 hover:text-blue-700 hover:underline cursor-pointer transition-colors"
                            >
                                devNectar
                            </a>
                        </p>
                    </form>
                </div>
            )}

            <style>{`
                @keyframes bounce-slow {
                    0%, 100% { transform: translateY(-5%); }
                    50% { transform: translateY(0); }
                }
                @keyframes pulse-ring {
                    0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.5); }
                    80%, 100% { box-shadow: 0 0 0 25px rgba(37, 99, 235, 0); }
                }
                @keyframes wave {
                    0%, 100% { transform: rotate(0deg); }
                    25% { transform: rotate(-15deg); }
                    75% { transform: rotate(15deg); }
                }
                @keyframes icon-click {
                    0% { transform: scale(1.05); }
                    100% { transform: scale(0.5); opacity: 0; }
                }
                @keyframes chat-slide-in {
                    0% { opacity: 0; transform: scale(0.95) translateY(20px); }
                    100% { opacity: 1; transform: scale(1) translateY(0); }
                }
                @keyframes mic-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
                    50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0); }
                }
                .animate-bounce-slow {
                    animation: bounce-slow 3s infinite ease-in-out;
                }
                .group:hover .group-hover\\:animate-ripple {
                    animation: ripple 1.2s infinite ease-out;
                }
                .animate-click-bounce {
                    animation: icon-click 250ms ease-out forwards;
                }
                .animate-chat-slide-in {
                    animation: chat-slide-in 400ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
                }
                .animate-mic-pulse {
                    animation: mic-pulse 1s infinite;
                }
                .scrollbar-custom::-webkit-scrollbar { height: 4px; }
                .scrollbar-custom::-webkit-scrollbar-track { background: #f9fafb; }
                .scrollbar-custom::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
                .scrollbar-custom::-webkit-scrollbar-thumb:hover { background: #3b82f6; }
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </>
    );
};

export default Chatbot;
